import { BLACK, type GameState, N, PHASE, type Phase, WHITE } from './types';

const STORAGE_KEY = 'konane:v1:state';
const HISTORY_KEY = 'konane:v1:history';

interface Persisted {
  state: GameState;
  history: GameState[];
}

export function saveGame(state: GameState, history: GameState[]): void {
  if (!hasStorage()) return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
  } catch (err) {
    // Quota exceeded, private mode, etc. Swallow — the game still works,
    // just without persistence.
    console.warn('Konane: failed to save game', err);
  }
}

export function loadGame(): Persisted | null {
  if (!hasStorage()) return null;
  try {
    const stateStr = localStorage.getItem(STORAGE_KEY);
    if (!stateStr) return null;
    const historyStr = localStorage.getItem(HISTORY_KEY);
    const rawState: unknown = JSON.parse(stateStr);
    const rawHistory: unknown = historyStr ? JSON.parse(historyStr) : [];
    if (!isValidState(rawState)) return null;
    if (!Array.isArray(rawHistory) || !rawHistory.every(isValidState)) return null;
    return { state: rawState, history: rawHistory };
  } catch (err) {
    console.warn('Konane: failed to load game', err);
    return null;
  }
}

export function clearSavedGame(): void {
  if (!hasStorage()) return;
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(HISTORY_KEY);
  } catch {
    // ignore
  }
}

function hasStorage(): boolean {
  try {
    return typeof localStorage !== 'undefined';
  } catch {
    return false;
  }
}

const VALID_PHASES: ReadonlySet<Phase> = new Set([
  PHASE.SETUP_B,
  PHASE.SETUP_W,
  PHASE.SELECT,
  PHASE.CAPTURE,
  PHASE.MULTI,
  PHASE.OVER,
]);

export function isValidState(s: unknown): s is GameState {
  if (!s || typeof s !== 'object') return false;
  const obj = s as Record<string, unknown>;
  if (!Array.isArray(obj.board) || obj.board.length !== N) return false;
  for (const row of obj.board) {
    if (!Array.isArray(row) || row.length !== N) return false;
    for (const cell of row) {
      if (cell !== BLACK && cell !== WHITE && cell !== null) return false;
    }
  }
  if (typeof obj.phase !== 'string' || !VALID_PHASES.has(obj.phase as Phase)) return false;
  if (obj.turn !== BLACK && obj.turn !== WHITE) return false;
  if (!obj.caps || typeof obj.caps !== 'object') return false;
  const caps = obj.caps as Record<string, unknown>;
  if (typeof caps.black !== 'number' || typeof caps.white !== 'number') return false;
  if (obj.winner !== null && obj.winner !== BLACK && obj.winner !== WHITE) return false;
  if (typeof obj.turnCount !== 'number') return false;
  return true;
}
